/* Paged carousels for the PDP rows, plus the Recently Viewed feed.
   Lulus advances a whole page at a time and shows "(n of m)" on the
   Complete the Look row, so that is what this does. */

class HvRow extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.countEl = this.querySelector('[data-count]');
    if (!this.viewport || !this.track) return;
    this.page = 0;
    this.init();
  }

  init() {
    const items = this.track.children;
    if (!items.length) return;

    this.step = () => {
      const first = items[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    };

    this.perPage = () => {
      const s = this.step();
      return s ? Math.max(1, Math.floor((this.viewport.clientWidth + 1) / s)) : 1;
    };

    /* Measured from how far the track can actually scroll rather than from the
       item count. On a phone Complete the Look stacks three cards per column
       (that is what Lulus does), so one "page" is one column, not one card -
       counting items would report four times too many pages. */
    this.pages = () => {
      const per = this.perPage() * this.step();
      if (!per) return 1;
      const room = this.viewport.scrollWidth - this.viewport.clientWidth;
      if (room <= 2) return 1;
      return Math.max(1, Math.ceil((room - 2) / per) + 1);
    };

    this.goTo = (p) => {
      const max = this.pages() - 1;
      this.page = Math.min(Math.max(p, 0), max);
      this.viewport.scrollTo({ left: this.page * this.perPage() * this.step(), behavior: 'smooth' });
      this.render();
    };

    this.render = () => {
      const max = this.pages() - 1;
      if (this.prevBtn) this.prevBtn.disabled = this.page <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.page >= max;
      if (this.countEl) this.countEl.textContent = ' (' + (this.page + 1) + ' of ' + (max + 1) + ')';
    };

    if (this._bound !== true) {
      this._bound = true;
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.page - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.page + 1));

      /* keep the page in sync when someone swipes instead of using the arrows */
      this.viewport.addEventListener('scroll', () => {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => {
          const per = this.perPage() * this.step();
          if (!per) return;
          this.page = Math.round(this.viewport.scrollLeft / per);
          this.render();
        });
      });

      window.addEventListener('resize', () => this.render());
    }

    this.render();
  }
}

if (!customElements.get('hv-row')) customElements.define('hv-row', HvRow);

/* ------------------------------------------------------- recently viewed */
/* Handles are kept in localStorage; each card is pulled from the product's own
   `card` alternate template so the markup stays in Liquid, not duplicated here. */
(async function recentlyViewed() {
  const KEY = 'hv:recently-viewed';
  const host = document.querySelector('[data-recently-viewed]');
  const current = document.body.getAttribute('data-product-handle');

  let list = [];
  try {
    list = JSON.parse(localStorage.getItem(KEY) || '[]');
  } catch (e) {
    list = [];
  }
  if (!Array.isArray(list)) list = [];

  const toShow = list.filter((h) => h && h !== current).slice(0, 12);

  if (current) {
    const next = [current].concat(list.filter((h) => h !== current)).slice(0, 20);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch (e) {
      /* private mode - not worth breaking the page over */
    }
  }

  if (!host || toShow.length < 2) return;
  const track = host.querySelector('[data-track]');
  if (!track) return;

  const cards = await Promise.all(
    toShow.map((h) =>
      fetch('/products/' + encodeURIComponent(h) + '?view=card')
        .then((r) => (r.ok ? r.text() : ''))
        .catch(() => '')
    )
  );

  const html = cards.filter(Boolean).map((c) => '<li class="hvrow2__item">' + c + '</li>').join('');
  if (!html) return;
  track.innerHTML = html;
  host.hidden = false;
  if (host.init) host.init();
})();
